import type { ReactNode } from 'react'

interface IntakeSectionProps {
  num: number
  label: string
  title: string
  subtitle?: string
  pct?: number | null
  actions?: ReactNode
  /**
   * Whether this section is the currently-active stage. When false the
   * CSS `.section { display: none }` rule hides it. Matches the original
   * lorenza.html behaviour: one section at a time, the stepper navigates.
   * Defaults to `true` so calls without the prop (e.g. dashboard cards)
   * stay visible.
   */
  active?: boolean
  children: ReactNode
}

/**
 * Renders one numbered section card with the original `.section-head`
 * layout: big mono section-number, mono label eyebrow, large title,
 * optional subtitle, optional completion % pill on the right.
 *
 * The original FastAPI page shows ONE section at a time and the stepper
 * navigates between them. We mirror that with the `active` prop, which
 * toggles the `.active` class CSS uses to flip `display: none`/`block`.
 */
export function IntakeSection({
  num,
  label,
  title,
  subtitle,
  pct = null,
  actions,
  active = true,
  children,
}: IntakeSectionProps) {
  const numStr = String(num).padStart(2, '0')
  return (
    <section className={`section${active ? ' active' : ''}`}>
      <div className="section-head">
        <div className="section-num">
          {numStr}
          <span className="dot">.</span>
        </div>
        <div className="section-title-block">
          <div className="label">{label}</div>
          <h2>{title}</h2>
          {subtitle && <p className="subtitle">{subtitle}</p>}
        </div>
        {pct != null && (
          <div className="section-completion">
            <span className="pct">{pct}%</span>complete
          </div>
        )}
        {actions && <div className="section-actions">{actions}</div>}
      </div>
      <div className="section-body">{children}</div>
    </section>
  )
}
